// calls all apis for ya

import Axios from 'axios';
import moment from 'moment';
import { get, isEmpty, isEqual } from 'lodash';
import Cookies from 'universal-cookie';

import rootStore from '../store/store';
import { apiBaseUrl, constants } from '../constants/configuration';
import {
  defferedPingServerInSeconds,
  internetDisconnectivityHandledAPIEndpointBlacklist,
  internetErrorResponseStoreDispatchDelay,
  serverErrorCodes,
  storageTypes,
  tokenExcludedAPIEndpointBlacklist,
} from '../constants/globals';
import { apiErrorResponseAction } from '../actions/app.action';
import {
  checkInternetConnectivity,
  getAppInStartupPhase,
  isJWTTokenExpired,
  isNativeWebView,
} from './helpers';

import { getItem, setItem } from './storage';
import { differenceBetween } from './fudrLocalizationHelper';
import { getIsApiRequestOnHold, setIsApiRequestOnHold } from './helpers';

const { store, persistor } = rootStore;

const TYPE_JSON = 'application/json';

let pendingRequests = [];
let refreshPromise = null;

export const buildAPIInstance = ({ contentType = TYPE_JSON }) => {
  const contextualTimeStamp = moment().format();
  const authData = getItem(storageTypes.localStorage, 'AuthData');
  const accessToken = get(authData, 'accessToken', '');
  const isApiRequestOnHold = getIsApiRequestOnHold();

  const headers = {
    common: {
      Accept: TYPE_JSON,
      'Content-Type': contentType,
      'x-source-type': 'client',
    },
    post: {
      'Content-Type': TYPE_JSON,
    },
  };

  const objAxios = Axios.create({
    baseURL: apiBaseUrl,
    headers,
  });

  objAxios.interceptors.request.use((config) => {
    if (!config.url) {
      return config;
    }

    if (
      isApiRequestOnHold &&
      !tokenExcludedAPIEndpointBlacklist.some((endpoint) => config.url.includes(endpoint))
    ) {
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject, config });
      });
    }
    if (
      !isEmpty(accessToken) &&
      !tokenExcludedAPIEndpointBlacklist.some((endpoint) => config.url.includes(endpoint))
    ) {
      const isTokenExpired = isJWTTokenExpired(accessToken);
      const isAppInStartupPhase = getAppInStartupPhase();
      if (isTokenExpired && isAppInStartupPhase) {
        const error = new Error('Request failed');
        error.response = {
          data: {
            isError: true,
            code: serverErrorCodes.invalidToken,
            message: 'Authentication error',
            detail: 'Authentication error',
            status: 'UNAUTHORIZED',
          },
        };
        let errorResponse = { ...error, config };
        handleAuthenticationError(errorResponse);
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject, config });
        });
      } else {
        config.headers['Authorization'] = `Bearer ${accessToken}`;
      }
    }

    const geoLocation = localStorage.getItem('geoLocation');
    if (geoLocation) {
      config.headers.common['location'] = geoLocation; // set updated geoLocation in req headers
    }
    // exempting internet connectivity logic for native as the same is already handled at the native level
    if (!isNativeWebView) {
      const requestURLPart = config.url;
      const isRequestURLBlacklisted = internetDisconnectivityHandledAPIEndpointBlacklist.some(
        (endpoint) => requestURLPart.indexOf(endpoint) > -1,
      );
      if (!isRequestURLBlacklisted) {
        const currentTimeStamp = getItem(storageTypes.sessionStorage, 'currentTimeStamp');
        const differenceInTimeStamps = differenceBetween(
          contextualTimeStamp,
          currentTimeStamp,
          'seconds',
        );
        if (differenceInTimeStamps > defferedPingServerInSeconds) {
          // update currentTimeStamp with latest time stamp
          setItem(storageTypes.sessionStorage, 'currentTimeStamp', moment().format());
          checkInternetConnectivity().then((isOnline) => {
            if (!isOnline) {
              // Generate a custom error response containing a user-friendly message to indicate internet disconnectivity.
              const errorResponse = {
                message: constants.globalErrorInfo.internetDisconnectivityErrorMessage,
              };
              console.error('Internet error response:', errorResponse);
              // Jump over all updates to errorResponse slice in redux store, if any, and then dispatch this one.
              setTimeout(() => {
                store.dispatch(apiErrorResponseAction(errorResponse));
              }, internetErrorResponseStoreDispatchDelay);
            }
          });
        }
      }
    }

    return config;
  });

  const handleTokenRefresh = async (authData) => {
    // if (!authData.refreshToken) return null;
    // Implement token refresh logic here

    // return token here
    const response = { isError: true };
    refreshPromise = response;
    return response;
  };

  const handleLogoutError = (error) => {
    const errorData = get(error, 'response.data', error);
    if (!errorData) {
      console.error('No error data found in error object:', error);
      return;
    }

    // Dispatching error only after bootstrapped (redux store is initialized)
    try {
      if (persistor.getState().bootstrapped) {
        store.dispatch(apiErrorResponseAction(errorData));
      } else {
        const unsubscribe = persistor.subscribe(() => {
          if (persistor.getState().bootstrapped) {
            unsubscribe(); // Stop listening after bootstrapped
            store.dispatch(apiErrorResponseAction(errorData));
          }
        });
      }
    } catch (error) {
      store.dispatch(apiErrorResponseAction(errorData));
    }
  };

  const handlePendingRequests = ({ newToken, error }) => {
    if (newToken) {
      for (const request of pendingRequests) {
        try {
          if (typeof request.resolve === 'function' && request.config.headers) {
            request.config.headers['Authorization'] = `Bearer ${newToken}`;
            request.resolve(request.config);
          } else {
            console.warn('Skipped malformed pending request (resolve):', request);
          }
        } catch (err) {
          console.error('Failed to resolve pending request:', err, request);
        }
      }
    } else {
      for (const request of pendingRequests) {
        try {
          if (typeof request.reject === 'function') {
            request.reject(error);
          } else {
            console.warn('Skipped malformed pending request (reject):', request);
          }
        } catch (err) {
          console.error('Failed to reject pending request:', err, request);
        }
      }
    }

    // Clear after processing all
    pendingRequests = [];
  };

  const handleAuthenticationError = async (error) => {
    let originalRequest = error.config;
    if (refreshPromise) return; // Avoid multiple refresh attempts
    setIsApiRequestOnHold(true);
    try {
      const authData = getItem(storageTypes.localStorage, 'AuthData');
      const refreshRes = await handleTokenRefresh(authData);
      const newToken = get(refreshRes, 'accessToken', '');

      if (newToken) {
        // Update the failed request with new token
        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

        // Process all pending requests with new token
        handlePendingRequests({ newToken });

        // Retry the original request
        return objAxios(originalRequest);
      } else {
        // Token refresh failed, reject all pending requests
        console.log('Token refresh failed');
        handlePendingRequests({ error });
        handleLogoutError(error);
        return Promise.reject(error);
      }
    } catch (refreshError) {
      // Error during refresh, reject all pending requests
      handlePendingRequests({ error: refreshError });
      handleLogoutError(refreshError);
      return Promise.reject(refreshError);
    }
  };

  objAxios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (isEqual(get(error, 'response.data.code'), serverErrorCodes.invalidToken)) {
        if (isApiRequestOnHold) {
          // Queue the request while token refresh is in progress
          return new Promise((resolve, reject) => {
            pendingRequests.push({ resolve, reject, config: originalRequest });
          });
        }
        await handleAuthenticationError(error);
      }
      return Promise.reject(error);
    },
  );

  return objAxios;
};

const client = (params = {}) => buildAPIInstance(params);

export default client;
